import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/common/CTASection";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { BLOG_POSTS, FAQS, RESOURCE_CATEGORIES } from "@/lib/content";

export default function Resources() {
  const [filter, setFilter] = useState("All");
  const cats = ["All", ...RESOURCE_CATEGORIES];
  const posts = filter === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === filter);
  const [featured, ...rest] = posts;

  return (
    <>
      <PageHero
        kicker="Resources"
        title="A knowledge center for India expansion."
        subtitle="Guides and articles to help you make confident decisions — because a good partner educates first."
      />

      {/* Filter */}
      <section className="border-b border-white/10 bg-[#131316] py-6" data-testid="resources-filter">
        <div className="container-px flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`resource-filter-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                filter === c ? "grad-accent text-[#1a0f00]" : "border border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="section-pad" data-testid="resources-list">
        <div className="container-px">
          {posts.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="resources-empty">
              <p className="font-head text-xl font-bold text-white">More guides on the way.</p>
              <p className="mt-3 text-sm text-zinc-400">We're publishing new {filter} resources soon. In the meantime, explore our other guides or book a consultation.</p>
              <button onClick={() => setFilter("All")} className="btn-ghost mt-6" data-testid="resources-empty-reset">View all resources</button>
            </div>
          )}
          {featured && (
            <Reveal>
              <Link to={`/resources/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="group mb-10 grid overflow-hidden rounded-[2rem] border border-white/10 bg-[#17171A] lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto">
                  <img src={featured.image} alt={featured.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#17171A]/70 to-transparent lg:bg-gradient-to-r" />
                </div>
                <div className="flex flex-col justify-center p-8 sm:p-12">
                  <span className="w-fit rounded-full border border-orange-500/20 bg-orange-500/5 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orange-300">{featured.category}</span>
                  <h3 className="mt-5 font-head text-2xl font-bold leading-tight text-white sm:text-3xl group-hover:text-orange-400">{featured.title}</h3>
                  <p className="mt-4 text-zinc-400">{featured.excerpt}</p>
                  <div className="mt-6 flex items-center gap-3 text-xs text-zinc-500">
                    <span>{featured.date}</span><span>·</span><span>{featured.readTime}</span>
                    <ArrowUpRight className="ml-auto h-5 w-5 text-orange-400 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p, i) => (
              <Reveal key={p.slug} i={i}>
                <Link to={`/resources/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 bg-white/[0.02] transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/30">
                  <div className="relative h-44 overflow-hidden">
                    <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="w-fit font-mono text-[10px] uppercase tracking-wider text-orange-400">{p.category}</span>
                    <h3 className="mt-3 font-head text-lg font-bold leading-snug text-white group-hover:text-orange-400">{p.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-400">{p.excerpt}</p>
                    <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500">{p.date} · {p.readTime}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad border-t border-white/10 bg-[#131316]" data-testid="faq-section">
        <div className="container-px max-w-3xl">
          <SectionHeading kicker="FAQs" title="Answers to common questions" center />
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.02] px-6">
                <AccordionTrigger className="text-left font-head text-base font-semibold text-white hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-zinc-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTASection />
    </>
  );
}
